import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, db, storage } from "../../firebase";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import NavBar from "../../components/NavBar";
import toast from "react-hot-toast";
import { Loader2, Plus } from "lucide-react";
import Homes from "./Home";

const New = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const { state } = useLocation();
  const {  code, usedCode } = state || {};

  const [formData, setFormData] = useState({
    purpose: "",
    guest: "",
    visitingDate: "",
    customPurpose: "",
    visitType: "",
    customVisitType: "",
    groupSize: "",
    customGroupSize: "",
    groupMemberNames: [],
    explanation: "",
    documents: [],
  });

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === "visitingDate") {
      formattedValue = new Date(value).toISOString();
    }

    // When groupSize changes, update the array of names
    if (name === "groupSize") {
      const newSize = parseInt(value) || 0;
      setFormData((prevData) => {
        const currentNames = prevData.groupMemberNames || [];
        const newNames = [];
        
        // Initialize array with empty strings up to the new size
        for (let i = 0; i < newSize; i++) {
          newNames[i] = currentNames[i] || "";
        }
        
        return {
          ...prevData,
          [name]: formattedValue,
          groupMemberNames: newNames,
        };
      });
      return;
    }

    setFormData((prevData) => ({ ...prevData, [name]: formattedValue }));
  };

  // Handle individual name field changes
  const handleNameChange = (index, value) => {
    setFormData((prevData) => {
      const newNames = [...(prevData.groupMemberNames || [])];
      newNames[index] = value;
      return { ...prevData, groupMemberNames: newNames };
    });
  };

  // Add a new input field (increases group size)
  const addMemberField = () => {
    setFormData((prevData) => {
      const currentSize = parseInt(prevData.groupSize) || 0;
      const newSize = currentSize + 1;
      const newNames = [...(prevData.groupMemberNames || [])];
      newNames.push(""); // Add empty string for new field
      
      return {
        ...prevData,
        groupSize: newSize.toString(),
        groupMemberNames: newNames,
      };
    });
  };

  // Handle file selection
  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 3) {
      toast.error("You can upload a maximum of 3 documents.");
      return;
    }
    setFormData((prevData) => ({ ...prevData, documents: files }));
  };

  // Upload documents to Firebase Storage
  const uploadDocuments = async (documents) => {
    if (!documents || documents.length === 0) return [];

    const uploadedLinks = [];
    for (const doc of documents) {
      try {
        const storageRef = ref(storage, `vestry_documents/${doc.name}`);
        await uploadBytes(storageRef, doc);
        const docUrl = await getDownloadURL(storageRef);
        uploadedLinks.push(docUrl);
      } catch (error) {
        toast.error("Error uploading document");
        console.error("Error uploading document:", error);
      }
    }
    return uploadedLinks;
  };

  // Add booking to Firestore
  const addBooking = async (bookingData) => {
    try {
      setLoading(true);
      const userId = auth.currentUser?.uid;
      if (!userId) {
        toast.error("User not authenticated.");
        return;
      }
  
      // Fetch user first_name and last_name
      const userRef = doc(db, `members/${userId}`);
      const userSnap = await getDoc(userRef);
      let first_name = "";
      let last_name = "";
      
      if (userSnap.exists()) {
        first_name = userSnap.data().first_name || "";
        last_name = userSnap.data().last_name || "";
      }
  
   
      if (usedCode && code) {
        // Assuming codes are stored in a 'codes' collection
        const codeRef = query(
          collection(db, "codes"), 
          where("code", "==", code)
        );
        const codeSnapshot = await getDocs(codeRef);
        
        if (!codeSnapshot.empty) {
          const codeDoc = codeSnapshot.docs[0];
          await updateDoc(doc(db, "codes", codeDoc.id), {
            used: true
          });
        }
      }
  
      const requiredFields = ["purpose", "guest", "visitType", "explanation"];
  
      if (bookingData.purpose === "Others")
        requiredFields.push("customPurpose");
      if (["Group", "Family"].includes(bookingData.visitType))
        requiredFields.push("groupSize");
      if (bookingData.groupSize === "Others")
        requiredFields.push("customGroupSize");
  
      for (const field of requiredFields) {
        if (!bookingData[field]) {
          setLoading(false);
          toast.error(
            `Please fill in the ${field
              .replace(/([A-Z])/g, " $1")
              .toLowerCase()}.`
          );
          return;
        }
      }

      // Ensure all person names are entered for Family/Group bookings
      if (["Group", "Family"].includes(bookingData.visitType)) {
        const expectedSize = parseInt(bookingData.groupSize, 10) || 0;
        const namesArray = (bookingData.groupMemberNames || []).map((n) =>
          (n || "").trim()
        );

        // Filter out any completely empty names
        const nonEmptyNames = namesArray.filter((n) => n.length > 0);

        if (expectedSize <= 0 || nonEmptyNames.length < expectedSize) {
          setLoading(false);
          toast.error(
            "Please enter a name for every person in your family/group (including yourself)."
          );
          return;
        }
      }
  
      const docLinks = await uploadDocuments(bookingData.documents);
      const bookingsRef = collection(db, `members/${userId}/bookings`);
  
      const visitingDateTimestamp = bookingData.visitingDate
        ? Timestamp.fromDate(new Date(bookingData.visitingDate))
        : null;
  
      // Add booking data
      await addDoc(bookingsRef, {
        purpose: bookingData.purpose,
        customPurpose: bookingData.customPurpose || null,
        visitType: bookingData.visitType,
        customVisitType: bookingData.customVisitType || null,
        groupSize: bookingData.groupSize || null,
        customGroupSize: bookingData.customGroupSize || null,
        groupMemberNames: bookingData.groupMemberNames || null,
        explanation: bookingData.explanation,
        doc: docLinks || [], 
        expired: bookingData.expired || null,
        createdAt: serverTimestamp(),
        bookee: userId,
        guest: bookingData.guest || null,
        first_name,
        last_name,
        visitingDate: visitingDateTimestamp, 
        status: "pending",
      });
  
      toast.success(
        "Your booking has been placed. Instructions will be sent to your email."
      );
      setLoading(false);
      navigate(-1);
    } catch (error) {
      setLoading(false);
      console.error("Error adding booking:", error);
      toast.error("Error adding booking!");
    }
  };

  return (
    <>
      <NavBar />
      <div
        className=" inset-0 flex items-center justify-center lg:min-h-screen "
        style={{
          backgroundImage: `url(${"https://i0.wp.com/www.angelsagainstabuse.org/wp-content/uploads/2018/11/blue-moving-flowing-abstract-waves-on-white-background-blurred-smooth-graphic-motion-design-video-animation-1920x1080_vhhueydte__F0000.png?fit=1920%2C1080&ssl=1"})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className=" p-6 rounded-lg shadow-lg w-full max-w-md mt-10">
          <h2 className="text-lg font-semibold mb-4">New Booking</h2>
          <div className="space-y-4">
            {/* Purpose of Booking */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Purpose of Booking
              </label>
              <select
                required
                name="purpose"
                value={formData.purpose}
                onChange={handleInputChange}
                className="mt-1 block w-full p-2 border rounded-md"
              >
                <option value="">Select Purpose</option>
                <option value="Thank You Father">Thank You Father</option>
                <option value="payment of tithe">Payment of Tithe</option>
                <option value="official report">Official Report</option>
                <option value="Others">Others</option>
              </select>
              {formData.purpose === "Others" && (
                <input
                  type="text"
                  name="customPurpose"
                  placeholder="Enter purpose"
                  value={formData.customPurpose}
                  onChange={handleInputChange}
                  className="mt-2 block w-full p-2 border rounded-md"
                />
              )}
            </div>

            {/* Visit date */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Choose a visiting date
              </label>
              <input
                type="date"
                name="visitingDate"
                onChange={handleInputChange}
                className="mt-1 block w-full p-2 border rounded-md"
              />
            </div>

            {/* Visit Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Visit Type
              </label>
              <select
                required
                name="visitType"
                value={formData.visitType}
                onChange={handleInputChange}
                className="mt-1 block w-full p-2 border rounded-md"
              >
                <option value="">Select Type</option>
                <option value="Group">Group</option>
                <option value="Personal">Personal</option>
                <option value="Family">Family</option>
                <option value="Others">Others</option>
              </select>
              {formData.visitType === "Others" && (
                <input
                  required
                  type="text"
                  name="customVisitType"
                  placeholder="Enter visit type"
                  value={formData.customVisitType}
                  onChange={handleInputChange}
                  className="mt-2 block w-full p-2 border rounded-md"
                />
              )}
            </div>

            {/* Group Size */}
            {(formData.visitType === "Group" ||
              formData.visitType === "Family") && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Group Size
                </label>
                <input
                    required
                    type="number"
                    name="groupSize"
                    placeholder="Enter your exact numerical strength"
                    value={formData.groupSize}
                    onChange={handleInputChange}
                    className="mt-2 block w-full p-2 border rounded-md"
                  />
                {/* <select
                  required
                  name="groupSize"
                  value={formData.groupSize}
                  onChange={handleInputChange}
                  className="mt-1 block w-full p-2 border rounded-md"
                >
                  <option value="">Select size</option>
                  <option value="1-10">1-10</option>
                  <option value="11-20">11-20</option>
                  <option value="21+">21+</option>
                  <option value="Others">Others</option>
                </select> */}
                {formData.groupSize === "Others" && (
                  <input
                    required
                    type="number"
                    name="customGroupSize"
                    placeholder="Enter your exact numerical strength"
                    value={formData.customGroupSize}
                    onChange={handleInputChange}
                    className="mt-2 block w-full p-2 border rounded-md"
                  />
                )}
              </div>
            )}

            {/* Group Member Names */}
            {((formData.visitType === "Group" || formData.visitType === "Family") && 
              formData.groupSize && parseInt(formData.groupSize) > 0) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Names of People Coming With You
                </label>
                <div className="space-y-2">
                  {formData.groupMemberNames.map((name, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => handleNameChange(index, e.target.value)}
                        placeholder={`Person ${index + 1} name`}
                        className="flex-1 p-2 border rounded-md"
                      />
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addMemberField}
                  className="mt-2 flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors text-sm"
                >
                  <Plus size={16} />
                  Add Person
                </button>
                <p className="mt-2 text-xs text-gray-500">
                  Please include your name and the names of all other people in your group. Group size: {formData.groupSize}
                </p>
              </div>
            )}

            {/* Explanation Textarea */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Explanation of Visit
              </label>
              <textarea
                required
                name="explanation"
                value={formData.explanation}
                onChange={handleInputChange}
                placeholder="Provide details about your visit"
                className="mt-2 block w-full p-2 border rounded-md"
              ></textarea>
            </div>

            {/* Explanation Textarea */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Are you a Member or Guest
              </label>
              <select
                  required
                  name="guest"
                  value={formData.guest}
                  onChange={handleInputChange}
                  className="mt-1 block w-full p-2 border rounded-md"
                >
                  <option value="">Select</option>
                  <option value="Member">Member</option>
                  <option value="Guest">Guest</option>
                </select>
            </div>

            {formData.visitType === "Group" && (
              <div className="">
                <label className="block text-sm font-medium text-gray-700">
                  Group Appreciation Payment
                  {/* : ONLY MAKE PAYMENT WHEN YOUR
                  BOOKING IS APPROVED✅. */}
                </label>
                <Homes />
              </div>
            )}
            {/* Document Upload */}
            <div className="mt-5">
              <label className="block text-sm font-medium text-gray-700">
                Upload Documents (Max 3)
              </label>
              <input
                type="file"
                name="documents"
                accept="application/pdf,image/*"
                multiple
                onChange={handleFileChange}
                className="mt-1 block w-full p-2 border rounded-md"
              />
              {formData.documents?.length > 0 && (
                <ul className="mt-2">
                  {formData.documents.map((doc, index) => (
                    <li key={index}>{doc.name}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Buttons */}
          <div className="mt-6 flex justify-end gap-4">
            <button
              onClick={() => {
                navigate(-1);
              }}
              className="px-4 bg-[#eee] py-2 bg-gray-300 rounded-md hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              disabled={loading}
              type="submit"
              className="px-4 bg-[#2563EB] py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              onClick={() => addBooking(formData)}
            >
              {loading ? (
                <Loader2 className="animate-spin mr-2 text-white" size={18} />
              ) : (
                <p>Submit</p>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default New;
